import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '../../lib/utils';
import { EASE_OUT, REVEAL_DURATION, WORD_STAGGER } from '../../lib/motion';

// "Transforming business through *AI-driven* technology" → words, with *…* marked as accent
export const parseAccent = text =>
  String(text)
    .split(/(\*[^*]+\*)/)
    .filter(Boolean)
    .flatMap(part => {
      const accent = part.length > 2 && part.startsWith('*') && part.endsWith('*');
      const clean = accent ? part.slice(1, -1) : part;
      return clean.split(/(\s+)/).filter(Boolean).map(word => ({ word, accent, space: /^\s+$/.test(word) }));
    });

export const plainText = text => String(text).replace(/\*/g, '');

const word = {
  hidden: { y: '115%' },
  show: i => ({ y: '0%', transition: { duration: REVEAL_DURATION, ease: EASE_OUT, delay: i * WORD_STAGGER } })
};

/*
 * Headline whose words slide up out of a mask. Words wrapped in *asterisks* are set in
 * the italic serif accent (pass accentClassName="accent-brand" for the gradient). Animates when scrolled
 * into view, or on mount with `animateOnMount` (held back while `play` is false).
 */
const SplitText = ({
  as = 'h2',
  text,
  className,
  accentClassName,
  delay = 0,
  animateOnMount = false,
  play = true,
  ...rest
}) => {
  const reduced = useReducedMotion();
  const words = useMemo(() => parseAccent(text), [text]);
  const Tag = motion[as] || motion.h2;
  let index = 0;

  const trigger = animateOnMount
    ? { initial: 'hidden', animate: play ? 'show' : 'hidden' }
    : { initial: 'hidden', whileInView: 'show', viewport: { once: true, amount: 0.4 } };

  return (
    <Tag className={cn(className)} {...(reduced ? {} : trigger)} {...rest}>
      {/* Read once, as plain text; the animated words below are hidden from assistive tech */}
      <span className="sr-only">{plainText(text)}</span>
      {words.map((w, i) => {
        if (w.space) return <React.Fragment key={i}> </React.Fragment>;
        const custom = index++ + delay / WORD_STAGGER;
        return (
          <span key={i} aria-hidden="true" className="inline-block overflow-hidden pb-[0.14em] -mb-[0.14em] align-top">
            <motion.span
              className={cn('inline-block', w.accent && cn('accent', accentClassName))}
              variants={reduced ? undefined : word}
              custom={custom}
            >
              {w.word}
            </motion.span>
          </span>
        );
      })}
    </Tag>
  );
};

export default SplitText;
